import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getStaticFileUrl } from "../../../config/apiConfig";
import { adminOrderService } from "../../../services/adminOrderService";
import {
  OrderDetails,
  OrderHistoryEntry,
  PaginatedHistoryResponse,
} from "../../../types/adminOrders";
import { formatDate } from "../../../utils/formatters";
import Currency from "../../common/Currency";

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  orderId: string;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  onClose,
  orderNumber,
  orderId,
}) => {
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [orderHistory, setOrderHistory] = useState<OrderHistoryEntry[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    hasMore: false,
    totalCount: 0,
  });

  const loadOrderDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await adminOrderService.getOrderDetails(orderId);
      setOrderDetails(details);
      // Reset history and pagination when loading new order
      setOrderHistory([]);
      setHistoryPagination({
        currentPage: 1,
        totalPages: 0,
        hasMore: false,
        totalCount: 0,
      });
      setHistoryExpanded(false);
    } catch (err) {
      setError(t("admin.failedToLoadOrderDetails"));
      console.error("Error loading order details:", err);
    } finally {
      setLoading(false);
    }
  }, [orderNumber, orderId]);

  useEffect(() => {
    if (open && orderNumber) {
      loadOrderDetails();
    }
  }, [open, orderNumber, loadOrderDetails]);

  const loadOrderHistory = async (loadMore = false) => {
    if (!orderDetails?.id) return; // Need order ID to load history

    // If loading more, don't load if already loading or no more pages
    if (loadMore && (historyLoading || !historyPagination.hasMore)) return;

    // If not loading more and already have data, don't reload
    if (!loadMore && orderHistory.length > 0) return;

    setHistoryLoading(true);
    try {
      const pageToLoad = loadMore ? historyPagination.currentPage + 1 : 1;
      const historyResponse: PaginatedHistoryResponse =
        await adminOrderService.getOrderHistory(
          orderDetails.id,
          pageToLoad,
          10
        );

      if (loadMore) {
        // Append new records to existing ones
        setOrderHistory((prev) => [...prev, ...historyResponse.items]);
      } else {
        // Replace with new records
        setOrderHistory(historyResponse.items);
      }

      // Update pagination info
      setHistoryPagination({
        currentPage: historyResponse.pageNumber,
        totalPages: historyResponse.totalPages,
        hasMore: historyResponse.hasNextPage,
        totalCount: historyResponse.totalCount,
      });
    } catch (err) {
      console.error("Error loading order history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryToggle = () => {
    setHistoryExpanded(!historyExpanded);
    if (!historyExpanded) {
      loadOrderHistory();
    }
  };

  // Compute TJS from stored exchange rate (fallback for old orders without dedicated TJS fields)
  const computeTjs = (usd: number | null | undefined): number | null => {
    if (usd == null || !orderDetails?.exchangeRateAtOrder) return null;
    return Math.round(usd * orderDetails.exchangeRateAtOrder * 100) / 100;
  };

  const getStatusColor = (status: string) => {
    const statusColors: {
      [key: string]:
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning";
    } = {
      Pending: "warning",
      Processing: "info",
      Shipped: "primary",
      Delivered: "success",
      Cancelled: "error",
      Refunded: "secondary",
    };
    return statusColors[status] || "default";
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  if (!orderDetails) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="info">{t("admin.noOrderDetailsFound")}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("admin.orderDetails.title")} - {orderDetails.orderNumber}
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={orderDetails.status}
              color={getStatusColor(orderDetails.status)}
              size="small"
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Order Information - All Order Table Fields */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.orderInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orderNumber")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderDetails.orderNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.fullName")}
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {orderDetails.fullName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.status")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.status}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.orderedDate")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.orderedDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.lastUpdated")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.updatedAt
                        ? formatDate(orderDetails.updatedAt)
                        : t("admin.never")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.itemCount")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.itemCount}
                    </Typography>
                  </Grid>
                  {orderDetails.deliveryInstructions && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.deliveryInstructions")}
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.deliveryInstructions}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Information Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.customerInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.fullName")}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {orderDetails.fullName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.email")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.phoneNumber")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.phoneNumber}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Pricing Information Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.pricingInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.totalPrice")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      <Currency amount={orderDetails.totalPrice} tjsAmount={orderDetails.totalPriceTjs ?? computeTjs(orderDetails.totalPrice)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.shippingCost")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.shippingCost} tjsAmount={orderDetails.shippingCostTjs ?? computeTjs(orderDetails.shippingCost)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.tax")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.tax} tjsAmount={orderDetails.taxTjs ?? computeTjs(orderDetails.tax)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.fees")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.fees || 0} tjsAmount={orderDetails.feesTjs ?? computeTjs(orderDetails.fees)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.totalCarrierCargoFee")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.totalCarrierCargoFee || 0} tjsAmount={computeTjs(orderDetails.totalCarrierCargoFee)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.discountAmount")}
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      -
                      <Currency
                        amount={
                          orderDetails.promotionDiscountAmount ||
                          orderDetails.discountAmount ||
                          0
                        }
                        tjsAmount={orderDetails.discountAmountTjs ?? computeTjs(orderDetails.promotionDiscountAmount || orderDetails.discountAmount)}
                      />
                    </Typography>
                    {orderDetails.promotionCode && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontFamily: "monospace", mt: 0.5 }}
                      >
                        {t("admin.code")}: {orderDetails.promotionCode}
                      </Typography>
                    )}
                  </Grid>
                  {orderDetails.promotionCode && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.promotionCode")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {orderDetails.promotionCode}
                      </Typography>
                      {orderDetails.promotionDiscountAmount && (
                        <Typography variant="caption" color="success.main">
                          {t("admin.saved")}:{" "}
                          <Currency
                            amount={orderDetails.promotionDiscountAmount}
                            tjsAmount={computeTjs(orderDetails.promotionDiscountAmount)}
                          />
                        </Typography>
                      )}
                    </Grid>
                  )}
                  {orderDetails.isReferredUser && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.referral.referredUser")}
                      </Typography>
                      <Chip
                        label={t("admin.referral.referredLabel")}
                        color="info"
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                      {orderDetails.referredByUserName && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          {t("admin.referral.referredBy")}: {orderDetails.referredByUserName}
                        </Typography>
                      )}
                      {orderDetails.referralCouponUsed && (
                        <Typography variant="caption" color="success.main" display="block">
                          {t("admin.referral.couponUsed")}: {orderDetails.referralCouponUsed}
                        </Typography>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Divider />
                    <Box mt={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.finalTotal")}
                      </Typography>
                      <Typography
                        variant="h5"
                        color="primary"
                        fontWeight="bold"
                      >
                        <Currency amount={orderDetails.finalTotal} tjsAmount={orderDetails.finalTotalTjs ?? computeTjs(orderDetails.finalTotal)} />
                      </Typography>
                    </Box>
                  </Grid>
                  {orderDetails.exchangeRateAtOrder && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {t("admin.exchangeRate")}: 1 USD = {orderDetails.exchangeRateAtOrder} c.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Delivery Information Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.deliveryInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.addressLine1")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.addressLine1}
                    </Typography>
                  </Grid>
                  {orderDetails.addressLine2 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.addressLine2")}
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.addressLine2}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.city")}
                    </Typography>
                    <Typography variant="body1">{orderDetails.city}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.region")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.region}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.postalCode")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.postalCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.country")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.country}
                    </Typography>
                  </Grid>
                  {orderDetails.pickupLocation && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.pickupLocation")}
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.pickupLocation}
                      </Typography>
                    </Grid>
                  )}
                  {orderDetails.deliveryInstructions && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.deliveryInstructions")}
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.deliveryInstructions}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Information Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("admin.paymentInformation")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.paymentMethod")}
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.paymentInfo.paymentMethod || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.paymentStatus")}
                    </Typography>
                    <Chip
                      label={
                        orderDetails.paymentInfo.paymentStatus || "Unknown"
                      }
                      size="small"
                      color={
                        orderDetails.paymentInfo.paymentStatus === "Paid"
                          ? "success"
                          : "warning"
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.amountPaid")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.paymentInfo.amountPaid} tjsAmount={orderDetails.paymentInfo.amountPaidTjs ?? computeTjs(orderDetails.paymentInfo.amountPaid)} />
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.refundAmount")}
                    </Typography>
                    <Typography variant="body1">
                      <Currency amount={orderDetails.paymentInfo.refundAmount} tjsAmount={computeTjs(orderDetails.paymentInfo.refundAmount)} />
                    </Typography>
                  </Grid>
                  {orderDetails.paymentInfo.cardLast4 && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.cardLast4")}
                      </Typography>
                      <Typography variant="body1">
                        •••• {orderDetails.paymentInfo.cardLast4}
                      </Typography>
                    </Grid>
                  )}
                  {orderDetails.paymentInfo.cardBrand && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.cardBrand")}
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.paymentInfo.cardBrand}
                      </Typography>
                    </Grid>
                  )}
                  {orderDetails.paymentInfo.transactionId && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.transactionId")}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontFamily="monospace"
                        fontSize="0.875rem"
                      >
                        {orderDetails.paymentInfo.transactionId}
                      </Typography>
                    </Grid>
                  )}
                  {orderDetails.paymentInfo.paymentDate && (
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        {t("admin.paymentDate")}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(orderDetails.paymentInfo.paymentDate)}
                      </Typography>
                    </Grid>
                  )}

                  {/* Manual Payment Fields (for Alif/DC Card) */}
                  {orderDetails.paymentInfo.manualPaymentTransactionId && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ mt: 1 }}
                        >
                          {t("admin.manualPaymentDetails")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          {t("admin.customerTransactionId")}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontFamily="monospace"
                          fontSize="0.875rem"
                        >
                          {orderDetails.paymentInfo.manualPaymentTransactionId}
                        </Typography>
                      </Grid>
                      {orderDetails.paymentInfo.manualPaymentCustomerPhone && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("admin.customerPhoneNumber")}
                          </Typography>
                          <Typography variant="body1">
                            {
                              orderDetails.paymentInfo
                                .manualPaymentCustomerPhone
                            }
                          </Typography>
                        </Grid>
                      )}
                      {orderDetails.paymentInfo.manualPaymentScreenshotUrl && (
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 1 }}
                          >
                            {t("admin.paymentScreenshot")}
                          </Typography>
                          <Box
                            component="img"
                            src={getStaticFileUrl(
                              orderDetails.paymentInfo
                                .manualPaymentScreenshotUrl
                            )}
                            alt={t("admin.paymentScreenshot")}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: 400,
                              border: "1px solid #ddd",
                              borderRadius: 1,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              window.open(
                                getStaticFileUrl(
                                  orderDetails.paymentInfo
                                    .manualPaymentScreenshotUrl!
                                ),
                                "_blank"
                              )
                            }
                          />
                        </Grid>
                      )}
                      {orderDetails.paymentInfo.manualPaymentVerifiedAt && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("admin.verifiedAt")}
                          </Typography>
                          <Typography variant="body1" color="success.main">
                            {formatDate(
                              orderDetails.paymentInfo.manualPaymentVerifiedAt
                            )}
                          </Typography>
                        </Grid>
                      )}
                      {orderDetails.paymentInfo.manualPaymentVerifiedBy && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            {t("admin.verifiedBy")}
                          </Typography>
                          <Typography variant="body1">
                            {orderDetails.paymentInfo.manualPaymentVerifiedBy}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Order History - Expandable Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <Typography variant="h6">{t("admin.orderHistory")}</Typography>
                  <IconButton
                    onClick={handleHistoryToggle}
                    color="primary"
                    size="small"
                  >
                    {historyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={historyExpanded}>
                  {historyLoading && orderHistory.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : orderHistory.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {t("admin.noHistoryAvailable")}
                    </Typography>
                  ) : (
                    <Box>
                      {/* History List */}
                      <List>
                        {orderHistory.map((entry, index) => (
                          <React.Fragment key={entry.id}>
                            <ListItem alignItems="flex-start">
                              <ListItemIcon>
                                <HistoryIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Typography
                                      variant="body1"
                                      fontWeight="medium"
                                    >
                                      {entry.action}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {formatDate(entry.createdAt)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                      paragraph
                                    >
                                      {entry.description}
                                    </Typography>
                                    {entry.performedBy && (
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {t("admin.performedBy")}: {entry.performedBy}
                                      </Typography>
                                    )}
                                    {entry.note && (
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        {t("admin.note")}: {entry.note}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < orderHistory.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>

                      {/* Pagination Info and Load More Button */}
                      <Box
                        mt={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="textSecondary">
                          {t("admin.showingOf", { count: orderHistory.length, total: historyPagination.totalCount })}
                          {historyPagination.totalPages > 1 && (
                            <span>
                              {" "}
                              ({t("admin.pageOf", { current: historyPagination.currentPage, total: historyPagination.totalPages })})
                            </span>
                          )}
                        </Typography>

                        {historyPagination.hasMore && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => loadOrderHistory(true)}
                            disabled={historyLoading}
                            startIcon={
                              historyLoading ? (
                                <CircularProgress size={16} />
                              ) : null
                            }
                          >
                            {historyLoading ? t("common.loading") : t("admin.loadMore")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
